import { useEffect, useState } from 'react';
import { RUOLI, utenze } from '../../api/utenze';
import type { DettaglioUtenza, ModificaUtenza, Ruolo } from '../../api/utenze';
import { formattaImporto } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';

interface Props {
  /** Null quando si sta creando: la scheda è la stessa, cambiano il titolo e cosa si salva. */
  utenzaId: string | null;
  onChiudi: () => void;
  onCambiata: () => void;
}

/*  La scheda di un'utenza, e la creazione di una nuova.

    Una scheda sola per i due casi, perché i campi sono gli stessi e tenerne due
    significherebbe che un campo aggiunto domani entra in una e non nell'altra. La
    differenza è il codice: in creazione si scrive, dopo si mostra e non si tocca —
    è l'identificativo con cui la persona entra, e cambiarlo mentre è collegata vorrebbe
    dire spostarle l'identità sotto i piedi.

    I permessi del ruolo scelto si mostrano man mano, ed è il punto della schermata: chi
    assegna un ruolo sta concedendo venti permessi, e senza vederli elencati sta scegliendo
    un'etichetta. «Ufficio» non dice che comprende l'emissione degli ordini.
*/
export function SchedaUtenza({ utenzaId, onChiudi, onCambiata }: Props) {
  const [utenza, setUtenza] = useState<DettaglioUtenza | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  /*  La password provvisoria appena generata. Sta qui e da nessun'altra parte: non si
      salva, non si rilegge, e sparisce chiudendo la scheda. Chi la genera la detta a
      voce a chi deve entrare; se la perde prima, ne genera un'altra.  */
  const [provvisoria, setProvvisoria] = useState<string | null>(null);

  /*  L'esito dell'ultimo invito mandato: «partito» oppure il perché no. Vive quanto la
      scheda, come la provvisoria qui sopra.  */
  const [invito, setInvito] = useState<string | null>(null);

  const [codice, setCodice] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [ruolo, setRuolo] = useState<Ruolo>('Operaio');
  const [sede, setSede] = useState('');
  const [costo, setCosto] = useState('');
  const [responsabile, setResponsabile] = useState(false);

  useEffect(() => {
    const chiudiConEsc = (e: KeyboardEvent) => e.key === 'Escape' && onChiudi();
    window.addEventListener('keydown', chiudiConEsc);
    return () => window.removeEventListener('keydown', chiudiConEsc);
  }, [onChiudi]);

  useEffect(() => {
    if (utenzaId === null) {
      setUtenza(null);
      setErrore(null);
      setCodice('');
      setNome('');
      setEmail('');
      setRuolo('Operaio');
      setSede('');
      setCosto('');
      return;
    }

    let annullato = false;
    setUtenza(null);
    setErrore(null);

    utenze
      .leggi(utenzaId)
      .then((u) => {
        if (annullato) return;
        mostra(u);
      })
      .catch((e: Error) => !annullato && setErrore(e.message));

    return () => {
      annullato = true;
    };
  }, [utenzaId]);

  function mostra(u: DettaglioUtenza) {
    setUtenza(u);
    setCodice(u.codice);
    setNome(u.nomeCompleto);
    setEmail(u.email ?? '');
    setRuolo(u.ruolo);
    setSede(u.codiceSede ?? '');
    setCosto(u.costoOrarioStandard === null ? '' : String(u.costoOrarioStandard).replace('.', ','));
    setResponsabile(u.responsabilePreventivi);
  }

  async function agisci(azione: () => Promise<DettaglioUtenza>): Promise<DettaglioUtenza | null> {
    setInCorso(true);
    setErrore(null);
    try {
      // La risposta del server sostituisce quello che c'è a schermo: ruolo, permessi e
      // stato li stabilisce il dominio, e ricalcolarli qui sarebbe una seconda
      // implementazione della stessa regola.
      const aggiornata = await azione();
      mostra(aggiornata);
      onCambiata();
      return aggiornata;
    } catch (e) {
      setErrore((e as Error).message);
      return null;
    } finally {
      setInCorso(false);
    }
  }

  const campi: ModificaUtenza = {
    nomeCompleto: nome.trim(),
    ruolo,
    email: email.trim() || null,
    codiceSede: sede.trim().toUpperCase() || null,
    /*  Il costo orario si scrive a mano, con la virgola. `numeroDecimale` restituisce
        null su un campo vuoto e non zero, ed è la distinzione che conta: vuoto vuol dire
        «non lo so», e le ore di quella persona entrano nel consuntivo contate e non
        valorizzate. Uno zero direbbe che il suo lavoro non costa niente.  */
    costoOrarioStandard: numeroDecimale(costo),
    responsabilePreventivi: responsabile,
  };

  const salvabile = campi.nomeCompleto.length > 0 && (utenzaId !== null || codice.trim().length > 0);

  async function mandaInvito(id: string) {
    setErrore(null);
    setProvvisoria(null);

    try {
      const esito = await utenze.mandaInvito(id);
      setInvito(
        esito.accodato
          ? `Invito mandato a ${campi.email ?? "l'indirizzo in anagrafica"}. ` +
            'Il link vale sette giorni e una volta sola.'
          : esito.perche,
      );
    } catch (guasto) {
      setInvito(null);
      setErrore((guasto as Error).message);
    }
  }

  /*  Creando un'utenza con un indirizzo, l'invito parte da solo. È quello che chi crea si
      aspetta che succeda — e non farlo è esattamente il difetto da cui è nata tutta
      questa parte: utenze create e credenziali mai arrivate a nessuno.

      Parte dopo la creazione e non dentro: se il salvataggio fallisce non c'è nessun
      invito da ritirare indietro, e se l'invito fallisce l'utenza c'è comunque — con
      scritto in scheda perché la mail non è partita e un bottone per riprovare.  */
  const salva = async () => {
    const appenaCreata = utenzaId === null;

    const salvata = await agisci(() =>
      appenaCreata
        ? utenze.crea({ ...campi, codice: codice.trim().toLowerCase() })
        : utenze.modifica(utenzaId, campi),
    );

    if (appenaCreata && salvata !== null && campi.email !== null) await mandaInvito(salvata.id);
  };

  if (errore !== null && utenza === null && utenzaId !== null) {
    return (
      <aside className="scheda">
        <p className="errore">{errore}</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </aside>
    );
  }

  if (utenzaId !== null && utenza === null) {
    return (
      <aside className="scheda">
        <p className="attesa">Apro l'utenza…</p>
      </aside>
    );
  }

  const permessiMostrati = utenza !== null && utenza.ruolo === ruolo ? utenza.permessi : null;

  return (
    <aside className="scheda scheda-utenza">
      <header>
        <div>
          <h2>{utenza === null ? 'Nuova utenza' : utenza.nomeCompleto}</h2>
          <p className="dettaglio">
            {utenza === null
              ? 'Il codice è quello con cui la persona entrerà in TeraWorks.'
              : `${utenza.codice}${utenza.attivo ? '' : ' — disattivata'}`}
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi">
          ×
        </button>
      </header>

      <div className="modulo-scheda">
        <label>
          <span>Codice</span>
          {utenza === null ? (
            <input
              value={codice}
              onChange={(e) => setCodice(e.target.value)}
              placeholder="gbianchi"
              autoComplete="off"
            />
          ) : (
            // Non si modifica: è l'identificativo con cui la persona è autenticata.
            <output className="fisso">{utenza.codice}</output>
          )}
        </label>

        <label>
          <span>Nome e cognome</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)} autoComplete="off" />
        </label>

        <label>
          <span>Posta elettronica</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="facoltativa"
            autoComplete="off"
          />
          <em className="aiuto">
            Facoltativa oggi. Diventerà il collegamento con l'utenza Microsoft 365 se
            l'autenticazione passerà da lì.
          </em>
        </label>

        <label>
          <span>Ruolo</span>
          <select value={ruolo} onChange={(e) => setRuolo(e.target.value as Ruolo)}>
            {RUOLI.map((r) => (
              <option key={r.valore} value={r.valore}>
                {r.valore}
              </option>
            ))}
          </select>
          <em className="aiuto">{RUOLI.find((r) => r.valore === ruolo)?.descrizione}</em>
        </label>

        <label>
          <span>Sede</span>
          <input
            value={sede}
            onChange={(e) => setSede(e.target.value)}
            placeholder="BI"
            maxLength={8}
            autoComplete="off"
          />
          <em className="aiuto">Entra nel numero dei documenti che questa persona emette.</em>
        </label>

        <label>
          <span>Costo orario</span>
          <input
            value={costo}
            onChange={(e) => setCosto(e.target.value)}
            placeholder="29,50"
            inputMode="decimal"
            autoComplete="off"
          />
          <em className="aiuto">
            {campi.costoOrarioStandard === null
              ? 'Vuoto vuol dire «non lo so»: le ore di questa persona entrano nel consuntivo contate e non valorizzate.'
              : `${formattaImporto(campi.costoOrarioStandard)} per ora di lavoro approvata.`}
          </em>
        </label>

        {/*  RESPONSABILE DEI PREVENTIVI.

             Chiesto da Andrea in demo da Galizzi il 01/10/2026: «l'approvazione può
             essere effettuata da un responsabile, e il responsabile può essere anche se
             stesso, basta che abbia il flag di responsabile preventivi in anagrafica
             utente».

             Sta sulla persona e non sul ruolo perché è qualcosa che si è: fra due tecnici
             con lo stesso ruolo uno lo è e l'altro no, ed è proprio quella la distinzione
             che serve. Toglie un solo divieto — firmare la propria offerta — e non
             concede niente d'altro: chiunque rilegge già le offerte dei colleghi.  */}
        <label className="campo-spunta">
          <span>
            <input
              type="checkbox"
              checked={responsabile}
              onChange={(e) => setResponsabile(e.target.checked)}
            />
            Responsabile dei preventivi
          </span>
          <em className="aiuto">
            {responsabile
              ? 'Può approvare un’offerta anche quando l’ha scritta lei.'
              : 'Le offerte che scrive questa persona le deve approvare qualcun altro.'}
          </em>
        </label>
      </div>

      {/*  I permessi del ruolo, elencati. Chi assegna «Ufficio» sta concedendo venti
           permessi: senza vederli sta scegliendo un'etichetta. Si mostrano solo quando
           combaciano con il ruolo salvato — dopo aver cambiato la tendina, l'elenco
           vecchio direbbe una cosa falsa, e inventarne uno nuovo qui vorrebbe dire
           riscrivere nel frontend la tabella dei profili del backend.  */}
      {permessiMostrati !== null ? (
        <details className="permessi">
          <summary>
            {permessiMostrati.length} permessi con il ruolo {utenza!.ruolo}
          </summary>
          <ul>
            {permessiMostrati.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </details>
      ) : (
        <p className="nota">
          Salva per vedere quali permessi porta il ruolo {ruolo}: l'elenco lo dichiara il
          server, non questa schermata.
        </p>
      )}

      {/*  Un'utenza senza password non è un difetto — è come nasce ognuna — ma è
           invisibile: la persona c'è in elenco, risulta attiva, e non entra. Detto qui, la
           domanda «perché il nuovo non riesce ad accedere» non arriva al telefono.  */}
      {utenza !== null && !utenza.haPassword && provvisoria === null && (
        <p className="nota">
          Non ha ancora una password: finché non gliene viene assegnata una, non può entrare.
        </p>
      )}

      {invito !== null && <p className="nota">{invito}</p>}

      {provvisoria !== null && (
        <div className="password-provvisoria">
          <p>
            Password provvisoria per {utenza?.nomeCompleto}. Dettagliela adesso: non si
            rilegge, e al primo accesso TeraWorks gliene chiederà una sua.
          </p>
          <code>{provvisoria}</code>
        </div>
      )}

      {errore !== null && <p className="errore">{errore}</p>}

      <div className="azioni">
        <button
          type="button"
          className="primario"
          disabled={inCorso || !salvabile}
          onClick={() => void salva()}
        >
          {inCorso ? 'Salvo…' : utenza === null ? 'Crea l’utenza' : 'Salva'}
        </button>

        {utenza !== null && utenza.attivo && !utenza.seiTu && (
          <button
            type="button"
            className="pericolo"
            disabled={inCorso}
            title="Non si cancella nessuno: le ore e i documenti restano col suo nome"
            onClick={() => void agisci(() => utenze.impostaAttiva(utenza.id, false))}
          >
            Disattiva
          </button>
        )}

        {utenza !== null && !utenza.attivo && (
          <button
            type="button"
            disabled={inCorso}
            onClick={() => void agisci(() => utenze.impostaAttiva(utenza.id, true))}
          >
            Riattiva
          </button>
        )}

        {/*  L'invito è la strada normale dove c'è la posta: la persona si sceglie la
             password da sé, e nessuno — nemmeno chi amministra — la conosce mai. Senza
             indirizzo in anagrafica non c'è dove mandarlo, e il bottone non compare: la
             password provvisoria qui accanto resta la strada per chi un indirizzo non ce
             l'ha, come l'operaio che entra solo dal telefono dell'azienda.  */}
        {utenza !== null && utenza.email !== null && utenza.attivo && (
          <button
            type="button"
            disabled={inCorso}
            title="Manda per mail il link con cui si sceglie la password da sé"
            onClick={() => void mandaInvito(utenza.id)}
          >
            {utenza.haPassword ? 'Rimanda l’invito' : 'Manda l’invito'}
          </button>
        )}

        {utenza !== null && (
          <button
            type="button"
            disabled={inCorso}
            title="Genera una password provvisoria da dettare: al primo accesso gliene verrà chiesta una sua"
            onClick={() => {
              setErrore(null);
              setInCorso(true);
              void utenze
                .reimpostaPassword(utenza.id)
                .then((esito) => {
                  setProvvisoria(esito.password);
                  onCambiata();
                })
                .catch((guasto: Error) => setErrore(guasto.message))
                .finally(() => setInCorso(false));
            }}
          >
            {utenza.haPassword ? 'Reimposta password' : 'Assegna password'}
          </button>
        )}

        {/*  Sulla propria utenza il bottone non c'è, e il perché è scritto: il dominio
             rifiuterebbe comunque la mossa, ma un bottone che dà sempre errore è un
             difetto anche quando il rifiuto è giusto.  */}
        {utenza?.seiTu === true && utenza.attivo && (
          <span className="nota">
            È la tua utenza: la disattivazione la deve fare un altro amministratore.
          </span>
        )}
      </div>
    </aside>
  );
}
